import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Truck, ArrowRight, CheckCircle2, Gauge, BookOpen, Compass, Rocket,
  ClipboardCheck, Bot, Calculator, FileSpreadsheet, Mail, ShieldCheck,
  Fuel, PiggyBank, Percent, TrendingUp, HelpCircle,
} from "lucide-react";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import FoundingBanner from "@/components/FoundingBanner";
import PricingTiers from "@/components/PricingTiers";
import { useFoundingStatus } from "@/hooks/useFoundingStatus";
import { PROGRAM_NAME } from "@/data/playbook";
import { STRATEGIST } from "@/data/strategist";

const COVER =
  "https://static.prod-images.emergentagent.com/jobs/ad103537-b988-4363-adb5-ef152c7e8f33/images/1ebfe7a44dbb96c3440c559a6a7d67209cfdb6dee462259249c701080055deee.png";

const PILLARS = [
  { icon: BookOpen, title: "Learn the business", text: "A clear 12-step foundation guide takes you from 'I want my own authority' to compliant, road-ready — in plain English." },
  { icon: Compass, title: "Know your numbers", text: "Grade your readiness, understand your true cost per mile, and learn to spot loads that actually make money." },
  { icon: Rocket, title: "Launch with a mentor", text: "When you're ready to roll, lean on a lane strategist who's built a real carrier — not a course seller." },
];

const PRO_TOOLS = [
  { icon: ClipboardCheck, title: "Simulated Load Board", text: "Practice booking freight and learn why a load is a win or a trap — before real money is on the line." },
  { icon: Bot, title: "AI Coach", text: "Ask owner-operator questions any time and get straight, practical answers built on your own numbers." },
  { icon: Calculator, title: "Cost-Per-Mile Calculator", text: "Find your real break-even and compare against anonymized peer benchmarks for your equipment." },
  { icon: FileSpreadsheet, title: "Paper Log Trainer", text: "Practice completing paper logs correctly for the days your ELD goes down — with instant rule checks." },
];

const FREE_TOOLS = [
  { icon: Calculator, title: "Cost-Per-Mile Calculator", text: "Find your true break-even rate before you book a load.", to: "/cost-per-mile-calculator", tag: "Free tool" },
  { icon: Fuel, title: "IFTA Fuel Tax Calculator", text: "Estimate your quarterly fuel tax, state by state.", to: "/ifta-fuel-tax-calculator", tag: "Free tool" },
  { icon: PiggyBank, title: "Startup Budget Planner", text: "See the real cash you need to launch your authority.", to: "/how-much-does-it-cost-to-start-a-trucking-company", tag: "Free tool" },
  { icon: Percent, title: "Quarterly Tax Estimator", text: "Know how much to set aside for taxes each quarter.", to: "/owner-operator-taxes", tag: "Free tool" },
  { icon: TrendingUp, title: "Owner-Operator Pay", text: "What owner-operators really make — gross vs. take-home.", to: "/how-much-do-owner-operators-make", tag: "Guide" },
  { icon: HelpCircle, title: "Owner-Operator FAQ", text: "Straight answers to the questions every new carrier asks.", to: "/owner-operator-faq", tag: "Guide" },
];

const FAQS = [
  { q: "Is the 12-Step Foundation Guide really free?", a: "Yes. Create a free Community account and the full 12-step guide, readiness diagnostic and progress tracking are yours at no cost — no card required." },
  { q: "What's the difference between Community and Pro?", a: "Community (free) teaches you the business: the 12-step guide, diagnostic and progress tracking. Pro adds the working tools — Simulated Load Board, AI Coach, Cost-Per-Mile Calculator and the Paper Log Trainer." },
  { q: "What is the Founding Member offer?", a: "The first 100 members to go Pro on the monthly plan lock in $29/mo for their first 60 days (then $49/mo). It's limited — once the 100 spots are gone, the offer closes." },
  { q: "Can I cancel anytime?", a: "Absolutely. Pro is a simple monthly or annual subscription you can cancel whenever you like — you keep access through the end of your billing period." },
  { q: "Do I need my own authority already?", a: "No. Whether you're still driving for someone else or already have your MC number, the guide meets you where you are and walks you forward step by step." },
  { q: "Is this legal, tax or financial advice?", a: "No. RouteHaul is educational mentorship built by an experienced lane strategist. Always confirm specifics with a licensed attorney, accountant or FMCSA before you file." },
];

function LandingHeader() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200" data-testid="landing-header">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="landing-brand" className="flex items-center gap-2 font-heading font-black text-lg tracking-tight text-slate-900 shrink-0">
          <Truck className="w-6 h-6 text-brand" strokeWidth={2.5} />
          <span>RouteHaul<span className="hidden sm:inline text-slate-400 font-bold"> Logistics Pathways</span></span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/login" data-testid="landing-login">
            <Button variant="ghost" className="h-10 px-4 rounded-full text-slate-700 font-bold text-xs sm:text-sm tracking-wide uppercase hover:bg-slate-100">Log in</Button>
          </Link>
          <Link to="/register" data-testid="landing-header-signup">
            <Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Get started</Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

const Section = ({ children, className = "", ...rest }) => (
  <section className={className} {...rest}>{children}</section>
);

export default function LandingPage() {
  const founding = useFoundingStatus();

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <LandingHeader />

      {/* Hero */}
      <Section className="relative overflow-hidden border-b border-slate-200" data-testid="landing-hero">
        <div className="absolute inset-0">
          <img src={COVER} alt="Semi truck on the open highway" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 sm:py-28 lg:py-32">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-bold text-brand mb-6">
              <span className="w-8 h-px bg-brand" /> {PROGRAM_NAME}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-slate-900 leading-[0.98]">
              Go from driver to <span className="text-brand">owning your own trucking business.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-700 max-w-xl">
              RouteHaul is the mentorship platform that guides freight owner-operators from their first
              readiness check to hauling under their own authority — with a proven 12-step guide, real
              tools, and a lane strategist in your corner. Create a free account to open the guide.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/register" data-testid="hero-signup-cta">
                <Button className="h-14 px-8 rounded-full bg-brand hover:bg-brand-hover text-white text-base font-bold group w-full sm:w-auto">
                  Get started free
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <GoogleSignInButton testid="hero-google-signin" label="Sign up with Google" className="h-14 w-full sm:w-auto" />
            </div>
            <p className="mt-4 text-sm text-slate-500">
              Already a member? <Link to="/login" data-testid="hero-login-link" className="text-brand font-bold hover:underline">Log in</Link>
            </p>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2 text-sm font-medium text-slate-600">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Free to start — no card</span>
              <span className="inline-flex items-center gap-1.5"><Gauge className="w-4 h-4 text-emerald-500" /> AI-graded readiness score</span>
              <span className="inline-flex items-center gap-1.5"><Truck className="w-4 h-4 text-emerald-500" /> Built by a lane strategist</span>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Founding Member offer */}
      {founding?.active && (
        <Section className="bg-slate-50 border-b border-slate-200" data-testid="landing-founding">
          <div className="max-w-5xl mx-auto px-6 py-14">
            <div className="text-center max-w-xl mx-auto mb-6">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-2">Limited launch offer</div>
              <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-black text-slate-900">Become a Founding Member</h2>
              <p className="mt-3 text-slate-600">
                The first 100 members to go Pro lock in a permanent founding badge and a special intro price.
                When the spots are gone, they're gone.
              </p>
            </div>
            <FoundingBanner founding={founding} />
            <div className="mt-6 flex justify-center">
              <Link to="/register" data-testid="founding-claim-cta">
                <Button className="h-12 px-8 rounded-full bg-brand hover:bg-brand-hover text-white font-bold group">
                  Claim your founding spot
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </Section>
      )}

      {/* What is RouteHaul */}
      <Section className="bg-white" data-testid="landing-about">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="max-w-2xl mb-12">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">What is RouteHaul?</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900 leading-tight">
              Buying a truck is easy. Building a business that survives isn't.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Most new carriers fail on the paperwork, the compliance and the cash flow — not the driving.
              RouteHaul is a practical, mobile-friendly mentorship platform that teaches you the business
              side first, then hands you the tools to run the numbers and launch with confidence.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              New to this? Start with our free guide on{" "}
              <Link to="/how-to-get-your-own-authority" data-testid="about-authority-link" className="text-brand font-bold hover:underline">
                how to get your own trucking authority
              </Link>.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.08 }}
                data-testid={`pillar-${i}`}
                className="rounded-2xl border border-slate-200 p-7 bg-white hover:border-brand transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-5">
                  <p.icon className="w-6 h-6 text-brand" strokeWidth={2.2} />
                </div>
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">{p.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Popular free tools */}
      <Section className="bg-slate-50 border-y border-slate-200" data-testid="landing-free-tools">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="max-w-2xl mb-12">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Popular free tools</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900 leading-tight">
              Try the numbers side for free — no account needed.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Real calculators and plain-English guides for owner-operators. Use them free, then create an
              account when you're ready for the full 12-Step guide and Pro tools.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FREE_TOOLS.map((t, i) => (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (i % 3) * 0.06 }}
              >
                <Link
                  to={t.to}
                  data-testid={`free-tool-${i}`}
                  className="group h-full rounded-2xl border border-slate-200 bg-white p-6 hover:border-brand hover:shadow-sm transition-all flex flex-col"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center">
                      <t.icon className="w-5 h-5 text-brand" strokeWidth={2.2} />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-brand bg-orange-50 px-2.5 py-1 rounded-full">{t.tag}</span>
                  </div>
                  <h3 className="font-heading text-base font-bold text-slate-900">{t.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed flex-1">{t.text}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-brand">
                    Open <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Pro tools */}
      <Section className="bg-slate-900 text-white" data-testid="landing-pro-tools">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="max-w-2xl mb-12">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Pro tools & features</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black leading-tight">
              The working tools that turn knowledge into profit.
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Upgrade to Pro when you're ready to put it to work — practical tools designed to help
              you price loads, protect margins and stay compliant.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {PRO_TOOLS.map((t, i) => (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                data-testid={`pro-tool-${i}`}
                className="rounded-2xl border border-white/10 bg-white/5 p-7 flex gap-5"
              >
                <div className="w-12 h-12 rounded-xl bg-brand/20 flex items-center justify-center shrink-0">
                  <t.icon className="w-6 h-6 text-brand" strokeWidth={2.2} />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold mb-1.5">{t.title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{t.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Pricing */}
      <Section className="bg-slate-50 border-y border-slate-200" data-testid="landing-pricing">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Simple pricing</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900">Start free. Go Pro when you're ready.</h2>
            <p className="mt-4 text-slate-600">Learn the business at no cost. Add the tools that run the numbers when it's time to put it to work.</p>
          </div>
          <PricingTiers showFounding={false} />
        </div>
      </Section>

      {/* Meet the strategist */}
      <Section className="bg-white" data-testid="landing-strategist">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-2">
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-20 h-20 bg-brand rounded-md z-0" />
              <div className="relative z-10 bg-slate-900 rounded-md p-8 text-white">
                <div className="w-16 h-16 rounded-full bg-brand flex items-center justify-center font-heading font-black text-2xl mb-5">LW</div>
                <h3 className="font-heading text-2xl font-black tracking-tight">{STRATEGIST.name}</h3>
                <p className="text-brand font-bold text-sm mt-1">{STRATEGIST.title}</p>
                <p className="text-slate-400 text-sm mt-0.5">{STRATEGIST.business}</p>
                <a href={`mailto:${STRATEGIST.email}`} data-testid="landing-strategist-email" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-brand mt-4 break-all">
                  <Mail className="w-4 h-4 shrink-0" /> {STRATEGIST.email}
                </a>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Meet your lane strategist</div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-black text-slate-900 leading-tight">
              Mentorship from someone who's actually done it.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">{STRATEGIST.blurb}</p>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="bg-slate-50 border-t border-slate-200" data-testid="landing-faq">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <div className="text-center mb-10">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">Questions</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900">Frequently asked</h2>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-slate-200">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-heading font-bold text-slate-900 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="bg-brand text-white" data-testid="landing-final-cta">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-black leading-tight">
            Ready to build a business, not just drive a truck?
          </h2>
          <p className="mt-5 text-orange-50 text-lg max-w-xl mx-auto">
            Create your free account and open the 12-Step Business Foundation Guide in the next minute.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" data-testid="final-signup-cta">
              <Button className="h-14 px-8 rounded-full bg-white text-brand hover:bg-orange-50 text-base font-bold group w-full sm:w-auto">
                Create my free account
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/login" data-testid="final-login-cta">
              <Button variant="outline" className="h-14 px-8 rounded-full border-white/60 bg-transparent text-white hover:bg-white/10 text-base font-bold w-full sm:w-auto">
                Log in
              </Button>
            </Link>
          </div>
          <p className="mt-6 inline-flex items-center gap-2 text-sm text-orange-50/90">
            <ShieldCheck className="w-4 h-4" /> Educational mentorship — not legal, tax or financial advice.
          </p>
        </div>
      </Section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-slate-400 flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} {PROGRAM_NAME}</span>
          <div className="flex items-center gap-4">
            <Link to="/how-to-get-your-own-authority" data-testid="landing-authority-link" className="hover:text-slate-900">How to Get Your Authority</Link>
            <Link to="/how-much-does-it-cost-to-start-a-trucking-company" data-testid="landing-startup-link" className="hover:text-slate-900">Startup Costs</Link>
            <Link to="/how-much-do-owner-operators-make" data-testid="landing-salary-link" className="hover:text-slate-900">Owner-Operator Pay</Link>
            <Link to="/owner-operator-taxes" data-testid="landing-taxes-link" className="hover:text-slate-900">Taxes</Link>
            <Link to="/cost-per-mile-calculator" data-testid="landing-cpm-link" className="hover:text-slate-900">CPM Calculator</Link>
            <Link to="/ifta-fuel-tax-calculator" data-testid="landing-ifta-link" className="hover:text-slate-900">IFTA Calculator</Link>
            <Link to="/owner-operator-faq" data-testid="landing-faq-link" className="hover:text-slate-900">FAQ</Link>
            <Link to="/trucking-glossary" data-testid="landing-glossary-link" className="hover:text-slate-900">Trucking Glossary</Link>
            <Link to="/about" data-testid="landing-about-link" className="hover:text-slate-900">About</Link>
            <Link to="/terms" data-testid="landing-terms-link" className="hover:text-slate-900">Terms &amp; Conditions</Link>
            <Link to="/privacy" data-testid="landing-privacy-link" className="hover:text-slate-900">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
